import { type RefObject, useEffect, useRef } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';

export type InfiniteListingLoaderProps = {
  hasMore: boolean;
  prefetchAfter: number;
  isLoading: boolean;
  error: boolean;
  paused: boolean;
  onLoadMore: () => void;
};

const PREFETCH_DISTANCE_PX = 400;

export const InfiniteListingLoader = ({
  hasMore,
  prefetchAfter,
  anchorRef,
  isLoading,
  error,
  paused,
  onLoadMore,
}: InfiniteListingLoaderProps & { anchorRef: RefObject<HTMLElement | null> }) => {
  const sentinelRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const element = prefetchAfter > 0 ? anchorRef.current : sentinelRef.current;
    if (!element || !hasMore || isLoading || error || paused) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      {
        root: element.closest('[data-map-results-scroll]'),
        rootMargin: `${PREFETCH_DISTANCE_PX}px 0px`,
      },
    );
    observer.observe(element);
    if (sentinelRef.current && sentinelRef.current !== element)
      observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, prefetchAfter, anchorRef, isLoading, error, paused, onLoadMore]);

  if (!hasMore && !error && !isLoading) return null;
  return (
    <>
      <Box ref={sentinelRef} aria-hidden className="h-px w-full" />
      {error && <Text role="alert">Couldn’t load more homes. Please try again.</Text>}
      {isLoading && <Text role="status">Loading more homes…</Text>}
      <Button
        analyticsName="map-load-more-homes"
        aria-label={error ? 'Retry loading homes' : 'Load more homes'}
        variant="secondary"
        disabled={isLoading || paused}
        onClick={onLoadMore}
      >
        {error ? 'Retry loading homes' : 'Load more homes'}
      </Button>
    </>
  );
};
